import {styled, Tabs, Text, TextTag} from '@volkswagen-onehub/components-core'
import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {anchors} from '../../../assets/css-vars/anchors'
import {boxedContainer} from '../../../assets/css-vars/gridUtils'
import {
    setCustomerType,
    setSelectedCarNames,
} from '../../../features/general/generalSlice'
import TrackingAction from '../../../tracking-manager'
import {useFeatureServices} from '../../../use-feature-services'
import Brands from '../../../utils/brand/brand'
import CustomerTypes, {
    CustomerTypesKeys,
    CustomerTypesUrl,
} from '../../../utils/customerTypes/customerTypes'
import {getFilterSearchResultPageloadData} from '../../../utils/tracking/getTrackingData'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import UrlRoutes from '../../../utils/url/urlRoutes'
import {RootState} from '../../rootReducer'
import {AppDispatch} from '../../thunk'
import filterByDealerCode from '../../../utils/filters/FilterByDealer'
import {shouldDisableBusinessTab} from '../../../utils/specialization/disableBusinessTab'
import {shouldDisablePrivateTab} from '../../../utils/specialization/disablePrivateTab'
import {clearHashFromUrl} from '../../../utils/url/hashParams'

const TabGroupWrapper = styled.section`
    padding-right: calc(var(--padding-x) * 1.5);
    padding-left: calc(var(--padding-x) * 1.5);
    margin-bottom: 100px;

    ${boxedContainer}
`

const NoFilteredCarsParagraph = styled.p`
    line-height: 1.5;

    ${anchors}
`

export default function TabGroup(): React.JSX.Element {
    const {t} = useTranslation('app')
    // tslint:disable-next-line:no-unnecessary-type-assertion
    const dispatch = useDispatch() as AppDispatch

    // tslint:disable-next-line:no-any
    const {'s2:history': historyService} = useFeatureServices() as any
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const customerTypeFromState: keyof typeof CustomerTypesUrl = useSelector(
        (state: RootState) => state.general.customerType
    )
    const dealerCode = useSelector(
        (state: RootState) => state.dealerData.dealerCode
    )
    const filteredModels = useSelector(
        (state: RootState) => state.general.filteredModels
    )
    const allModels = useSelector(
        (state: RootState) => state.general.modelsWithActivePromotions
    )
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const {tracking: trackingManager} = useFeatureServices()
    const noFilteredCarsMessage = t('listing.noPromosByCustomerType')
    const [defaultTabIndex, setDefaultTabIndex] = useState(0)
    const [userHasClickedTabs, setUserHasClickedTabs] = useState(false)
    const [disablePrivateTab, setDisablePrivateTab] = useState(false)
    const [disableBusinessTab, setDisableBusinessTab] = useState(false)
    const [disableBothTabs, setDisableBothTabs] = useState(false)

    const initialUrlProcessed = useRef(false)
    const isRedirecting = useRef(false)
    const manualTabChange = useRef(false)

    const location = useLocation()

    useEffect(() => {
        if (dealerCode) {
            const filteredCars = filterByDealerCode(dealerCode, allModels)
            const privateTabDisabled = shouldDisablePrivateTab(filteredCars)
            const businessTabDisabled = shouldDisableBusinessTab(filteredCars)

            setDisablePrivateTab(privateTabDisabled)
            setDisableBusinessTab(businessTabDisabled)
            setDisableBothTabs(privateTabDisabled && businessTabDisabled)
        }
    }, [dealerCode, allModels])

    useEffect(() => {
        if (!initialUrlProcessed.current && location && location.pathname) {
            initialUrlProcessed.current = true

            const customerFromCurrentLocation = location.pathname.replace(
                `/${UrlRoutes.CUSTOMER_TYPE}/`,
                ''
            ) as CustomerTypesUrl

            const urlValues = Object.values(CustomerTypesUrl)
            const index = urlValues.indexOf(customerFromCurrentLocation)

            if (index !== -1) {
                const customerTypeKeys = Object.keys(CustomerTypes)
                // eslint-disable-next-line security/detect-object-injection
                const customerTypeKey = customerTypeKeys[
                    index
                ] as keyof typeof CustomerTypes
                // eslint-disable-next-line security/detect-object-injection
                const customerType = CustomerTypes[customerTypeKey]

                setDefaultTabIndex(index)
                dispatch(
                    setCustomerType(customerType as unknown as CustomerTypes)
                )
            }
        }
    }, [location.pathname, dispatch])

    useEffect(() => {
        if (isRedirecting.current) {
            return
        }

        if (!disableBothTabs && initialUrlProcessed.current) {
            isRedirecting.current = true

            try {
                if (
                    customerTypeFromState === CustomerTypes.PRIVATE &&
                    disablePrivateTab &&
                    !disableBusinessTab
                ) {
                    setDefaultTabIndex(
                        Object.keys(CustomerTypes).indexOf(
                            CustomerTypesKeys.PRIVATE
                        )
                    )
                    dispatch(setCustomerType(CustomerTypes.BUSINESS))
                    historyService.history.replace(
                        `/${UrlRoutes.CUSTOMER_TYPE}/${
                            CustomerTypesUrl[CustomerTypes.BUSINESS]
                        }`
                    )
                } else if (
                    customerTypeFromState === CustomerTypes.BUSINESS &&
                    disableBusinessTab &&
                    !disablePrivateTab
                ) {
                    setDefaultTabIndex(
                        Object.keys(CustomerTypes).indexOf(
                            CustomerTypesKeys.PRIVATE
                        )
                    )
                    dispatch(setCustomerType(CustomerTypes.PRIVATE))
                    historyService.history.replace(
                        `/${UrlRoutes.CUSTOMER_TYPE}/${
                            CustomerTypesUrl[CustomerTypes.PRIVATE]
                        }`
                    )
                }
            } finally {
                setTimeout(() => {
                    isRedirecting.current = false
                }, 100)
            }
        }
    }, [
        disablePrivateTab,
        disableBusinessTab,
        disableBothTabs,
        customerTypeFromState,
        dispatch,
        historyService,
    ])

    useEffect(() => {
        if (!initialUrlProcessed.current) {
            return
        }

        if (brandFromState === Brands.VIC && customerTypeFromState) {
            // eslint-disable-next-line security/detect-object-injection
            const currentUrl = `/${UrlRoutes.CUSTOMER_TYPE}/${CustomerTypesUrl[customerTypeFromState]}`

            if (location.pathname !== currentUrl) {
                historyService.history.replace(currentUrl)
            }
            if (userHasClickedTabs && checkTrackingManager(trackingManager)) {
                setTracking(
                    trackingManager,
                    TrackingAction.VWBasic_FilterSearchResult_Pageload,
                    getFilterSearchResultPageloadData(
                        filteredModels.length,
                        trackingBrand,
                        isShowroom
                    )
                )
            }

            manualTabChange.current = false
        }
    }, [
        customerTypeFromState,
        brandFromState,
        historyService,
        location.pathname,
        userHasClickedTabs,
        trackingManager,
        filteredModels.length,
        trackingBrand,
        isShowroom,
    ])

    const setSelectedTab = (index: number) => {
        dispatch(setSelectedCarNames([]))
        clearHashFromUrl(historyService)
        const chosenCustomerType = Object.values(CustomerTypes)[index]

        if (chosenCustomerType !== customerTypeFromState) {
            manualTabChange.current = true
            setUserHasClickedTabs(true)
            setDefaultTabIndex(index)
            dispatch(setCustomerType(chosenCustomerType))
        }
    }

    const filteredTabs = [
        {
            key: CustomerTypes.PRIVATE,
            title: <Text tag={TextTag.span}>{t('tabs.private.label')}</Text>,
            content:
                filteredModels.length === 0 ? (
                    <NoFilteredCarsParagraph
                        dangerouslySetInnerHTML={{
                            __html: noFilteredCarsMessage,
                        }}
                    />
                ) : (
                    <></>
                ),
        },
        {
            key: CustomerTypes.BUSINESS,
            title: <Text tag={TextTag.span}>{t('tabs.business.label')}</Text>,
            content:
                filteredModels.length === 0 ? (
                    <div>
                        <NoFilteredCarsParagraph
                            dangerouslySetInnerHTML={{
                                __html: noFilteredCarsMessage,
                            }}
                        />
                    </div>
                ) : (
                    <></>
                ),
        },
    ]

    const visibleTabs = disableBothTabs
        ? filteredTabs
        : filteredTabs.filter((tab) => {
              if (tab.key === CustomerTypes.PRIVATE && disablePrivateTab) {
                  return false
              }

              return !(tab.key === CustomerTypes.BUSINESS && disableBusinessTab)
          })

    return (
        <TabGroupWrapper>
            <Tabs
                idPrefix="customerTypes"
                defaultIndex={defaultTabIndex}
                onChange={(index) => {
                    setSelectedTab(index)
                }}
            >
                {visibleTabs}
            </Tabs>
        </TabGroupWrapper>
    )
}
